import {
  publicIdSchema,
  type VisitSource,
  type VisitSourceCreateInput,
  visitSourceSchema,
} from "@rosc/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { z } from "zod";
import { referenceClient } from "@/lib/orpc/client";
import { useVisitScope, type VisitScope } from "@/lib/visit-queries";

export type { VisitSource };

const listSchema = z.strictObject({ sources: z.array(visitSourceSchema) });
// Under the visit-reads prefix, so the list is dropped on account change.
export const visitSourcesKey = (
  scope: VisitScope | null,
  visitPublicId: string,
) =>
  [
    "visit-reads",
    scope?.uid ?? "",
    scope?.accountPublicId ?? "",
    scope?.generation ?? 0,
    "sources",
    visitPublicId,
  ] as const;

const sameVisit = (source: VisitSource, visitPublicId: string): VisitSource => {
  if (source.visitPublicId !== visitPublicId)
    throw Error("Source does not belong to this visit");
  return source;
};

export function useVisitSources(visitPublicId: string, enabled = true) {
  const scope = useVisitScope();
  const client = useQueryClient();
  const key = visitSourcesKey(scope, visitPublicId);
  const context = { expectedUserUid: scope?.uid };
  const list = useQuery({
    queryKey: key,
    enabled:
      enabled && !!scope && publicIdSchema.safeParse(visitPublicId).success,
    retry: false,
    // Sources change only through this panel, which updates the cache.
    staleTime: 30_000,
    queryFn: async ({ signal }) =>
      listSchema
        .parse(
          await referenceClient.listVisitSources(
            { params: { public_id: String(visitPublicId) }, query: {} },
            { context, signal },
          ),
        )
        .sources.map((source) => sameVisit(source, visitPublicId)),
  });
  const create = useMutation({
    mutationFn: async ({
      body,
      key: idempotencyKey,
    }: {
      body: VisitSourceCreateInput;
      key: string;
    }) =>
      sameVisit(
        visitSourceSchema.parse(
          await referenceClient.createVisitSource(
            {
              params: { public_id: String(visitPublicId) },
              query: {},
              headers: { "idempotency-key": idempotencyKey },
              body,
            },
            { context },
          ),
        ),
        visitPublicId,
      ),
    onSuccess: (source) => {
      client.setQueryData<VisitSource[]>(key, (sources = []) => [
        ...sources.filter((item) => item.id !== source.id),
        source,
      ]);
    },
  });
  const remove = useMutation({
    mutationFn: async (sourceId: number) => {
      await referenceClient.deleteVisitSource(
        {
          params: {
            public_id: String(visitPublicId),
            sourceId: String(sourceId),
          },
          query: {},
        },
        { context },
      );
      return sourceId;
    },
    onSettled: () => client.invalidateQueries({ queryKey: key }),
    onSuccess: (sourceId) => {
      client.setQueryData<VisitSource[]>(key, (sources = []) =>
        sources.filter((item) => item.id !== sourceId),
      );
    },
  });
  return {
    sources: list.data ?? [],
    loading: list.isPending && list.fetchStatus !== "idle",
    failed: list.isError,
    retry: () => void list.refetch(),
    create: create.mutateAsync,
    remove: remove.mutateAsync,
  };
}

// Which sources the assistant and new notes use. New sources start
// included; the clinician can leave any of them out.
export function useSourceSelection(sources: VisitSource[]) {
  const [excluded, setExcluded] = useState<ReadonlySet<number>>(new Set());
  useEffect(() => {
    setExcluded((current) => {
      const ids = new Set(sources.map((source) => source.id));
      const kept = [...current].filter((id) => ids.has(id));
      return kept.length === current.size ? current : new Set(kept);
    });
  }, [sources]);
  const selectedIds = sources
    .filter((source) => !excluded.has(source.id))
    .map((source) => source.id);
  const setIncluded = (id: number, included: boolean): void => {
    setExcluded((current) => {
      const next = new Set(current);
      if (included) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  return {
    selectedIds,
    isIncluded: (id: number) => !excluded.has(id),
    setIncluded,
  };
}

// The workspace's sources and selection.
export type WorkspaceSources = ReturnType<typeof useVisitSources> &
  ReturnType<typeof useSourceSelection>;
export function useWorkspaceSources(visitPublicId: string): WorkspaceSources {
  const sources = useVisitSources(
    visitPublicId,
    publicIdSchema.safeParse(visitPublicId).success,
  );
  const selection = useSourceSelection(sources.sources);
  return { ...sources, ...selection };
}
